<template>
  <nav
    aria-label="page navigation"
    class="pagination-wrapper"
  >
    <span class="pagination-total">{{ t('common.page.total', {
      detail0: total
    }) }}</span>
    <ul ref="paginationEl" class="pagination"></ul>
    <div class="pagination-jump-wrapper">
      <label>
        <i18n-t keypath="common.page.go">
          <template #detail0>
            <input
            type="number"
            autocomplete="off"
            min="1"
            :max="totalPages"
            step="1"
            v-model="jumpPage"
            class="pagination-jump"
            style="width: 3rem"
            @change="handleJumpChange"
          />
          </template>
         </i18n-t>
      </label>
    </div>
    <div class="pagination-size-selector" v-if="showSizeSelector">
      <span class="me-2">{{ t('file.itemsPerPage') }}</span>
      <CustomSelect
        name="pageSize"
        v-model="currentPageSize"
        :options="pageSizeOptionsMemo"
        style="height: 28px !important; font-size: 12px; line-height: 1;"
      />
    </div>
  </nav>
</template>

<script setup>
import { ref, watch, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import CustomSelect from './Form/CustomSelect.vue';

const { t, locale } = useI18n();
const route = useRoute();

const props = defineProps({
  total: {
    type: Number,
    default: 0
  },
  currentPage: {
    type: Number,
    default: 1
  },
  pageSize: {
    type: Number,
    default: 10
  },
  pageSizeOptions: {
    type: Array,
    default: () => [10, 20, 50, 100]
  },
  storageKey: {
    type: String,
    default: ''
  },
  showSizeSelector: {
    type: Boolean,
    default: false
  }
});

const pageSizeOptionsMemo = computed(() => {
  return props.pageSizeOptions.map(size => ({
    value: size,
    label: `${size}`
  }));
});

const emit = defineEmits(['update:currentPage', 'update:pageSize', 'pageChange', 'pageSizeChange']);

const paginationEl = ref(null);
const jumpPage = ref(props.currentPage);
const currentPageSize = ref(props.pageSize);

watch(currentPageSize, (newSize) => {
  if (newSize !== props.pageSize) {
    handlePageSizeChange();
  }
});

const totalPages = computed(() => {
  return Math.ceil(props.total / currentPageSize.value)
})

const getStorageKey = () => {
  if (props.storageKey) {
    return `pagination_size_${props.storageKey}`;
  }
  return `pagination_size_${route.name || route.path}`;
};

const loadPageSizeFromStorage = () => {
  if (!props.showSizeSelector) return;
  
  try {
    const storageKey = getStorageKey();
    const savedSize = localStorage.getItem(storageKey);
    if (savedSize) {
      const size = parseInt(savedSize, 10);
      if (props.pageSizeOptions.includes(size)) {
        currentPageSize.value = size;
        emit('update:pageSize', size);
        emit('pageSizeChange', size);
      }
    }
  } catch (error) {
    console.error('Failed to load page size from storage:', error);
  }
};

const savePageSizeToStorage = (size) => {
  if (!props.showSizeSelector) return;
  
  try {
    const storageKey = getStorageKey();
    localStorage.setItem(storageKey, size.toString());
  } catch (error) {
    console.error('Failed to save page size to storage:', error);
  }
};

const handlePageSizeChange = () => {
  const newSize = currentPageSize.value;
  savePageSizeToStorage(newSize);
  
  emit('update:currentPage', 1);
  emit('update:pageSize', newSize);
  emit('pageSizeChange', newSize);
  emit('pageChange', {
    page: 1,
    pageSize: newSize
  });
};

watch([() => props.currentPage, totalPages], (newVal) => {
  jumpPage.value = newVal[0];
  updatePagination();
});

watch(() => props.pageSize, (newSize) => {
  currentPageSize.value = newSize;
});

watch(locale, () => {
  updatePagination();
});

onMounted(() => {
  loadPageSizeFromStorage();
  updatePagination();
});

const disablePaginationItem = (className) => {
  const item = paginationEl.value.querySelector(`.${className}`);
  if (item) {
    item.classList.add('disabled');
    const link = item.querySelector('a');
    if (link) {
      link.style.pointerEvents = 'none';
    }
  }
};

const updatePagination = () => {
  const { currentPage } = props;
  console.log(t('common.page.first'))
  if (totalPages.value >= 1) {
    paginationEl.value.innerHTML =
      `<li class="page-item page-first"><a class="page-link" href="#">${t('common.page.first')}</a></li>
       <li class="page-item page-divider" style="color:#00365b33">|</li>
       <li class="page-item page-prev"><a class="page-link" href="#">${t('common.page.previous')}</a></li>`;

    for (let i = 1; i <= totalPages.value; i++) {
      const liEl = document.createElement('li');
      liEl.classList.add('page-item', 'page-number');

      if (i === Number(currentPage)) {
        liEl.classList.add('active');
      }

      // add gap
      if (i !== totalPages.value) {
        liEl.style.marginRight = '0.5rem';
      }
      liEl.style.backgroundColor = '#0068B014';

      const aEl = document.createElement('a');
      aEl.classList.add('page-link');
      aEl.style.color = '#0068B099';

      aEl.href = '#';
      aEl.textContent = i;
      liEl.appendChild(aEl);
      paginationEl.value.appendChild(liEl);
    }
    
    paginationEl.value.innerHTML +=
      `<li class="page-item page-next"><a class="page-link" href="#">${t('common.page.next')}</a></li>
       <li class="page-item page-divider" style="color:#00365b33">|</li>
       <li class="page-item page-last"><a class="page-link" href="#">${t('common.page.last')}</a></li>`;

    if (totalPages.value === 1) {
      const classes = ['page-prev', 'page-next', 'page-first', 'page-last'];
      classes.forEach(className => {
        disablePaginationItem(className);
      });
    }

    // only show 5 pages mostly
    // format page item to 1,2,...,length-1,length
    if (totalPages.value > 5) {
      const cursor = Number(currentPage);
      let validPages = [cursor, cursor + 1, cursor + 2, cursor + 3, cursor + 4, cursor + 5];
      let centerPage = cursor + 4;
      
      if (cursor > totalPages.value- 5) {
        validPages = [totalPages.value - 5, totalPages.value - 4, totalPages.value - 3, totalPages.value - 2, totalPages.value - 1, totalPages.value];
        centerPage = totalPages.value - 6;
      }

      const pageItems = paginationEl.value.querySelectorAll('.page-number');
      const pageItemsArr = Array.from(pageItems);
      pageItemsArr.forEach((item, index) => {
        if (!validPages.includes(index + 1)) {
          item.style.display = 'none';
        }

        if (index === centerPage) {
          item.querySelector('a').textContent = '…';
          item.querySelector('a').style.paddingLeft = '9px';
          item.querySelector('a').style.paddingRight = '9px';
          item.firstChild.setAttribute('disabled', '');
          item.firstChild.style.pointerEvents = 'none';
        }
      });
    }

    if (currentPage === 1) {
      disablePaginationItem('page-prev');
      disablePaginationItem('page-first');
    }

    if (currentPage === totalPages.value) {
      disablePaginationItem('page-next');
      disablePaginationItem('page-last');
    }

    paginationEl.value.onclick = handlePaginationClick;
  }else {
    paginationEl.value.innerHTML = ''
  }
};

const handlePaginationClick = (e) => {
  e.preventDefault();

  if (e.target.tagName === 'A') {
    const classes = e.target.parentElement.classList;
    let newPage = Number(e.target.textContent);

    if (classes.contains('page-first')) {
      newPage = 1;
    }

    if (classes.contains('page-prev')) {
      newPage = props.currentPage - 1;
      if (newPage < 1) {
        newPage = 1;
      }
    }

    if (classes.contains('page-next')) {
      newPage = props.currentPage + 1;
      if (newPage > totalPages.value) {
        newPage = props.totalPages;
      }
    }

    if (classes.contains('page-last')) {
      newPage = totalPages.value;
    }

    changePage(newPage);
  }
};

const handleJumpChange = () => {
  let value = Number(jumpPage.value);
  if (value > totalPages.value) {
    value = totalPages.value;
    jumpPage.value = value;
  }

  if (value < 1) {
    value = 1;
    jumpPage.value = value;
  }

  changePage(value);
};

const changePage = (page) => {
  if (page !== props.currentPage) {
    emit('update:currentPage', page);
    emit('pageChange', {
      page,
      pageSize: props.pageSize
    });
  }
};
</script>

<style scoped>
.pagination-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1.5rem;
  font-size: 0.875rem;
}

.pagination {
  display: flex;
  padding-left: 0;
  list-style: none;
  margin-bottom: 0;
}

.pagination :deep(.page-item.disabled) {
  background-color: initial !important;
} 

.page-item.active .page-link {
  background-color: #0068B0;
  color: white !important;
}

.page-item.disabled .page-link {
  color: #6c757d;
  pointer-events: none;
  cursor: auto;
}

.page-link {
  position: relative;
  display: block;
  padding: 0.375rem 0.75rem;
  text-decoration: none;
  background-color: #0068B014;
  border-radius: 0.25rem;
}

.pagination-size-selector {
  display: flex;
  align-items: center;
}

@media (max-width: 768px) {
  .pagination-wrapper {
    flex-direction: column;
    gap: 0.75rem;
  }

  
  .pagination {
    flex-wrap: wrap;
    justify-content: center;
  }


}
</style> 